/**
 * Shopify-style CTA config for storefront sections (single source of truth for editor + render).
 */

import {
    classifySectionButtonLink,
    sanitizeSectionButtonHref,
} from "@/lib/sections/sectionButtonLink";
import {
    applyAdaptiveCtaStyling,
    DEFAULT_CTA_FALLBACK_LABEL,
    ensureMeaningfulCtaLabel,
    prioritizeCtasByIntent,
} from "@/lib/sections/sectionCtaIntelligence";
import type { ResolveSectionButtonsOptions } from "@/lib/sections/sectionCtaIntelligence";

export type { ResolveSectionButtonsOptions, SectionCtaSurface } from "@/lib/sections/sectionCtaIntelligence";

export type SectionButtonVariant = "primary" | "secondary" | "outline" | "ghost";
export type SectionButtonSize = "sm" | "md" | "lg";
export type SectionButtonAlign = "left" | "center" | "right";

export interface SectionButtonConfig {
    text: string;
    link: string;
    variant: SectionButtonVariant;
    size: SectionButtonSize;
    align: SectionButtonAlign;
    fullWidth: boolean;
    icon?: string;
    openInNewTab?: boolean;
    disabled?: boolean;
    /** Editor / async actions; can be overridden by `SectionConfigButton` `loading` prop. */
    loading?: boolean;
}

export const DEFAULT_SECTION_BUTTON: SectionButtonConfig = {
    text: "",
    link: "/shop",
    variant: "primary",
    size: "lg",
    align: "center",
    fullWidth: false,
};

function trimStr(v: unknown): string {
    return typeof v === "string" ? v.trim() : "";
}

/** Map editor / legacy `solid` | `outline` to SectionButtonVariant. */
export function legacyStyleToVariant(
    style?: unknown,
    explicit?: unknown
): SectionButtonVariant {
    const v = (explicit ?? style) as string | undefined;
    if (v === "outline") return "outline";
    if (v === "ghost") return "ghost";
    if (v === "secondary") return "secondary";
    if (v === "primary" || v === "solid" || v === "default") return "primary";
    return "primary";
}

function safeButtonLink(link: string): string {
    const t = trimStr(link) || DEFAULT_SECTION_BUTTON.link;
    const cls = classifySectionButtonLink(sanitizeSectionButtonHref(t));
    if (cls.kind === "invalid") return "#";
    return t;
}

function linkFromFlatContent(content: Record<string, unknown>): string {
    const bl = trimStr(content.buttonLink);
    if (bl) return safeButtonLink(bl);
    const cl = trimStr(content.ctaLink);
    if (cl) return safeButtonLink(cl);
    const aid = trimStr(content.anchorId);
    if (aid) {
        if (aid.startsWith("/") || aid.startsWith("#")) return safeButtonLink(aid);
        return safeButtonLink(`#${aid}`);
    }
    return DEFAULT_SECTION_BUTTON.link;
}

/** Normalize partial object (e.g. array item from editor). */
export function normalizeSectionButton(
    partial: Partial<SectionButtonConfig> & Record<string, unknown>
): SectionButtonConfig {
    const sizeRaw = partial.size as string | undefined;
    const size: SectionButtonSize =
        sizeRaw === "sm" ? "sm" : sizeRaw === "lg" ? "lg" : "md";

    const alignRaw = partial.align as string | undefined;
    const align: SectionButtonAlign =
        alignRaw === "left" || alignRaw === "right" ? alignRaw : "center";

    const linkRaw =
        trimStr(partial.link) ||
        trimStr(partial.url) ||
        trimStr(partial.href) ||
        DEFAULT_SECTION_BUTTON.link;

    return {
        text: trimStr(partial.text),
        link: safeButtonLink(linkRaw),
        variant: legacyStyleToVariant(partial.style, partial.variant),
        size,
        align,
        fullWidth: Boolean(partial.fullWidth),
        icon: trimStr(partial.icon) || undefined,
        openInNewTab: partial.openInNewTab === undefined ? undefined : Boolean(partial.openInNewTab),
        disabled: Boolean(partial.disabled),
        loading: Boolean(partial.loading),
    };
}

/** Build config from flat section content (hero, banners, etc.). */
export function sectionButtonFromFlatContent(
    content: Record<string, unknown>
): SectionButtonConfig {
    const sizeRaw = content.buttonSize as string | undefined;
    const size: SectionButtonSize =
        sizeRaw === "sm" ? "sm" : sizeRaw === "lg" ? "lg" : "md";

    const alignRaw = content.buttonAlign as string | undefined;
    const align: SectionButtonAlign =
        alignRaw === "left" || alignRaw === "right" ? alignRaw : "center";

    return normalizeSectionButton({
        text: content.buttonText ?? content.ctaText ?? content.cta ?? content.ctaLabel,
        link: linkFromFlatContent(content),
        variant: legacyStyleToVariant(content.buttonStyle, content.buttonVariant),
        size,
        align,
        fullWidth: content.buttonFullWidth,
        icon: content.buttonIcon,
        openInNewTab: content.openInNewTab,
        disabled: content.buttonDisabled,
        loading: content.buttonLoading,
    });
}

/** Stable key for deduping CTAs (case-insensitive label + trimmed link). */
export function sectionButtonDedupeKey(text: string, link: string): string {
    return `${text.trim().toLowerCase()}\u0000${link.trim()}`;
}

/**
 * Drop empty text and duplicate (text + link) pairs (first wins).
 */
export function dedupeSectionButtons(buttons: SectionButtonConfig[]): SectionButtonConfig[] {
    const seen = new Set<string>();
    const out: SectionButtonConfig[] = [];
    for (const b of buttons) {
        if (!b.text?.trim()) continue;
        const k = sectionButtonDedupeKey(b.text, b.link);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(b);
    }
    return out;
}

type TaggedButton = { b: SectionButtonConfig; explicit: boolean };

function finalizeTagged(
    tagged: TaggedButton[],
    content: Record<string, unknown>,
    options?: ResolveSectionButtonsOptions
): SectionButtonConfig[] {
    try {
        const fallback = options?.fallbackLabel ?? DEFAULT_CTA_FALLBACK_LABEL;
        let t = tagged;

        if (options?.enrichLabels !== false) {
            t = t.map((row) => ({
                ...row,
                b: {
                    ...row.b,
                    text: ensureMeaningfulCtaLabel(row.b.text, fallback),
                },
            }));
        }

        t = t.filter((row) => Boolean(row.b.text?.trim()));

        const seen = new Set<string>();
        t = t.filter((row) => {
            const k = sectionButtonDedupeKey(row.b.text, row.b.link);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });

        if (options?.prioritizeByIntent !== false && t.length > 1) {
            t = prioritizeCtasByIntent(t);
        }

        let buttons = t.map((row) => row.b);
        const explicitMask = t.map((row) => row.explicit);

        const adaptiveOn =
            options?.sectionKind != null &&
            options?.adaptiveDefaults !== false &&
            content.ctaAdaptiveDefaults !== false;

        if (adaptiveOn && options?.sectionKind) {
            buttons = applyAdaptiveCtaStyling(buttons, options.sectionKind, explicitMask);
        }

        return buttons;
    } catch {
        return [];
    }
}

/**
 * Prefer `buttons[]` when present; otherwise flat CTA.
 * Pipeline: normalize → enrich labels → dedupe → intent sort → adaptive styling (optional).
 */
export function resolveSectionButtonsFromContent(
    content: Record<string, unknown>,
    options?: ResolveSectionButtonsOptions
): SectionButtonConfig[] {
    let tagged: TaggedButton[] = [];

    try {
        const raw = content.buttons;
        if (Array.isArray(raw) && raw.length > 0) {
            for (const item of raw) {
                try {
                    const obj =
                        typeof item === "object" && item !== null
                            ? (item as Record<string, unknown>)
                            : {};
                    const explicit =
                        Object.prototype.hasOwnProperty.call(obj, "variant") ||
                        Object.prototype.hasOwnProperty.call(obj, "style");
                    tagged.push({ b: normalizeSectionButton(obj), explicit });
                } catch {
                    /* malformed row — skip */
                }
            }
        }

        if (tagged.length === 0) {
            const flatExplicit =
                Object.prototype.hasOwnProperty.call(content, "buttonVariant") ||
                Object.prototype.hasOwnProperty.call(content, "buttonStyle");
            try {
                const one = sectionButtonFromFlatContent(content);
                tagged = [{ b: one, explicit: flatExplicit }];
            } catch {
                tagged = [];
            }
        }
    } catch {
        tagged = [];
    }

    return finalizeTagged(tagged, content, options);
}

/** Alias for apps / sections — same as `resolveSectionButtonsFromContent`. */
export const resolveButtons = resolveSectionButtonsFromContent;

export type SectionButtonsRowAlign = SectionButtonAlign;

export function rowAlignToJustifyClass(align: SectionButtonsRowAlign): string {
    switch (align) {
        case "left":
            return "justify-start";
        case "right":
            return "justify-end";
        default:
            return "justify-center";
    }
}

const BUTTON_CONTENT_KEYS = [
    "buttonText",
    "buttonLink",
    "buttonStyle",
    "buttonVariant",
    "buttonSize",
    "buttonAlign",
    "buttonFullWidth",
    "buttonIcon",
    "openInNewTab",
    "buttonDisabled",
    "buttonLoading",
    "buttons",
    "ctaText",
    "ctaLink",
    "cta",
    "ctaLabel",
    "anchorId",
    "ctaAdaptiveDefaults",
] as const;

/** Extract editor props relevant to CTAs (for resolveSectionButtonsFromContent). */
export function pickButtonContentFromProps(props: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const k of BUTTON_CONTENT_KEYS) {
        if (k in props && props[k] !== undefined) {
            out[k] = props[k];
        }
    }
    return out;
}
